import {$controller} from "rapid/index";
import {cloneDeep, orderBy, forEach} from "lodash";
const dependencies = ["$Configuration", "$companies"];

import {ChangeNameController} from "../quick-setup/change-name/change-name";
import ChangeNameTemplate from "../quick-setup/change-name/change-name.html";

import {AddSceneController} from "./add-scene/add-scene";
import AddSceneTemplate from "./add-scene/add-scene.html";
import AddPromptingSceneTemplate from "./add-scene-prompting/add-scene-prompting.html";

const REPLICATE_PROMPTING_FEATURE = "replicate_prompting";
const VIDEO_CHROMAKEY_FEATURE = "video_chromakey";

class ScenesListController extends $controller {

    constructor($injector, $scope) {
        "ngInject";
        super($injector, dependencies);
        this.$scope = $scope;
        this.activeScroll = null;
        this.expandedStates = {};
        this.captureModeErrors = {};
        this.aiFeatureErrors = {};
        
        /**
         * Gets the text to display for selected features in the dropdown
         * @param {Object} scene - The scene object
         * @returns {string} The text to display
         */
        this.getSelectedFeaturesText = function(scene) {
            if (!scene.selectedFeature) {
                return "Select Feature";
            }
            return scene.selectedFeature.name;
        };
        
        /**
         * AI feature groups configuration
         * Defines the structure and available options for AI features
         */
        this.aiFeatureGroups = [
            {
                group: "Background AI",
                features: [
                    { name: "Remove Background (online)", value: "remove_bg_online" },
                    { name: "Remove Background (offline)", value: "remove_bg_offline" },
                    { name: "Remove Background (video)", value: "remove_bg_video" }
                ]
            },
            {
                group: "Prompting AI",
                features: [
                    { name: "Remove Background (promting)", value: REPLICATE_PROMPTING_FEATURE }
                ]
            },
            {
                group: "Face AI",
                features: [
                    { name: "Aging Filter", value: "wandershare_api" },
                    { name: "FaceSwap", value: "faceswap" },
                    { name: "Multi FaceSwap", value: "multi_faceswap" },
                    { name: "AI Avatar", value: "ai_avatar" }
                ]
            },
            {
                group: "General",
                features: [
                    { name: "2D to 3D", value: "2d_to_3d" }
                ]
            },
            {
                group: "Video",
                features: [
                    { name: "Video Chromakey", value: VIDEO_CHROMAKEY_FEATURE }
                ]
            }
        ];
        this.dropdownOpen = {};

        /**
         * Toggles the dropdown state for a specific scene
         * @param {number} sceneIndex - Index of the scene
         */
        this.toggleDropdown = function(sceneIndex) {
            this.dropdownOpen[sceneIndex] = !this.dropdownOpen[sceneIndex];
        };

        /**
         * Validates capture modes for a scene
         * Ensures at least one capture mode is enabled
         * @param {Object} scene - The scene object
         * @param {number} index - Index of the scene
         * @returns {boolean} Whether validation passed
         */
        this.validateCaptureModes = function(scene, index) {
            const hasEnabledMode = scene.captureModes.some(mode => mode.enabled);
            this.captureModeErrors[index] = !hasEnabledMode;
            return hasEnabledMode;
        };

        /**
         * Validates AI feature compatibility with capture modes
         * Implements specific rules for different feature types
         * @param {Object} scene - The scene object
         * @param {number} index - Index of the scene
         * @returns {boolean} Whether validation passed
         */
        this.validateAIFeatureWithCaptureModes = function(scene, index) {
            if (!scene.selectedFeature) {
                this.aiFeatureErrors[index] = null;
                return true;
            }

            const feature = scene.selectedFeature.value;
            const isPhotoEnabled = scene.captureModes[0].enabled;
            const isGifEnabled = scene.captureModes[1].enabled;
            const isBoomerangEnabled = scene.captureModes[2].enabled;
            const isVideoEnabled = scene.captureModes[3].enabled;

            // Rule 1: FaceSwap features and Aging Filter only work with Photo mode
            if (feature === "faceswap" || feature === "multi_faceswap" || feature === "wandershare_api") {
                if (!isPhotoEnabled) {
                    this.aiFeatureErrors[index] = "FaceSwap features and Aging Filter can only be used with Photo mode.";
                    return false;
                }
                if (isGifEnabled || isBoomerangEnabled || isVideoEnabled) {
                    this.aiFeatureErrors[index] = "FaceSwap features and Aging Filter can only be used with Photo mode. Please disable other capture modes.";
                    return false;
                }
            }

            // Rule 2: Remove BG (online/offline) cannot work with Boomerang or Video
            if ((feature === "remove_bg_online" || feature === "remove_bg_offline") && 
                (isBoomerangEnabled || isVideoEnabled)) {
                this.aiFeatureErrors[index] = "Remove Background (online/offline) cannot be used with Boomerang or Video modes.";
                return false;
            }

            // Rule 3: Prompting remove background works with Photo mode only
            if (feature === REPLICATE_PROMPTING_FEATURE) {
                if (!isPhotoEnabled) {
                    this.aiFeatureErrors[index] = "Remove Background (promting) can only be used with Photo mode.";
                    return false;
                }
                if (isGifEnabled || isBoomerangEnabled || isVideoEnabled) {
                    this.aiFeatureErrors[index] = "Remove Background (promting) can only be used with Photo mode. Please disable other capture modes.";
                    return false;
                }
            }

            // Rule 4: Remove BG Video only works with Boomerang or Video
            if (feature === "remove_bg_video") {
                if (!(isBoomerangEnabled || isVideoEnabled)) {
                    this.aiFeatureErrors[index] = "Remove Background (video) can only be used with Boomerang or Video modes.";
                    return false;
                }
                if (isPhotoEnabled || isGifEnabled) {
                    this.aiFeatureErrors[index] = "Remove Background (video) can only be used with Boomerang or Video modes. Please disable other capture modes.";
                    return false;
                }
            }

            // Rule 5: Video Chromakey only works with Video capture mode
            if (feature === VIDEO_CHROMAKEY_FEATURE) {
                if (!isVideoEnabled) {
                    this.aiFeatureErrors[index] = "Video Chromakey can only be used with Video capture mode.";
                    return false;
                }
                if (isPhotoEnabled || isGifEnabled || isBoomerangEnabled) {
                    this.aiFeatureErrors[index] = "Video Chromakey can only be used with Video capture mode. Please disable other capture modes.";
                    return false;
                }
            }

            this.aiFeatureErrors[index] = null;
            return true;
        };

        /**
         * Handles capture mode changes
         * Validates capture modes and AI feature compatibility
         * @param {Object} scene - The scene object
         * @param {number} index - Index of the changed capture mode
         */
        this.onCaptureModeChange = function(scene, index) {
            if (!this.validateCaptureModes(scene, index)) {
                this.$mdToast.show(
                    this.$mdToast.simple()
                        .textContent("Please select at least one capture mode for this scene.")
                        .position("top right")
                        .hideDelay(3000)
                );
            }
            
            const sceneIndex = this.model.scenesList.indexOf(scene);
            this.validateAIFeatureWithCaptureModes(scene, sceneIndex);
            this.normalizeSceneFeatureState(scene, sceneIndex);
        };

        /**
         * Handles AI feature selection
         * @param {Object} scene - The scene object
         * @param {Object} feature - The selected feature
         */
        this.selectFeature = function(scene, feature) {
            if (this.isFeatureDisabled(feature, scene)) {
                return;
            }

            const sceneIndex = this.model.scenesList.indexOf(scene);
            
            if (this.isFeatureSelected(scene, feature)) {
                this.clearPromptingSettings(scene);
                scene.selectedFeature = null;
                this.aiFeatureErrors[sceneIndex] = null;
            } else {
                const promptingSettings = scene.selectedFeature
                    && scene.selectedFeature.value === REPLICATE_PROMPTING_FEATURE
                    && scene.selectedFeature.promting_settings
                    ? angular.copy(scene.selectedFeature.promting_settings)
                    : undefined;

                scene.selectedFeature = angular.copy(feature);
                if (feature.value === REPLICATE_PROMPTING_FEATURE && promptingSettings) {
                    scene.selectedFeature.promting_settings = promptingSettings;
                } else {
                    this.clearPromptingSettings(scene);
                }
                // Validate and show message if needed, but don't prevent selection
                this.validateAIFeatureWithCaptureModes(scene, sceneIndex);
            }
            
            this.dropdownOpen[sceneIndex] = false;
        };

        /**
         * Checks if a feature is selected for a scene
         * @param {Object} scene - The scene object
         * @param {Object} feature - The feature to check
         * @returns {boolean} Whether the feature is selected
         */
        this.isFeatureSelected = function(scene, feature) {
            if (!scene.selectedFeature) return false;
            return scene.selectedFeature.value === feature.value;
        };

        /**
         * Checks if a feature is disabled
         * @param {Object} feature - The feature to check
         * @returns {boolean} Whether the feature is disabled
         */
        this.isFeatureDisabled = function(feature, scene) {
            if (feature.value === REPLICATE_PROMPTING_FEATURE) {
                return !this.isPromptingFeatureAvailable(scene);
            }

            if (feature.value === VIDEO_CHROMAKEY_FEATURE) {
                if (!scene || !scene.captureModes || !scene.captureModes[3] || !scene.captureModes[3].enabled) {
                    return true;
                }
                if (!this.activeFeatures) {
                    return true;
                }
                return !this.activeFeatures.some(function(item) {
                    return item.feature === VIDEO_CHROMAKEY_FEATURE && item.active;
                });
            }

            if (!this.activeFeatures) return false;
            return !this.activeFeatures.some(function(item) {
                return item.feature === feature.value && item.active;
            });
        };

        /**
         * Clears the selected feature for a scene
         * @param {Object} scene - The scene object
         */
        this.clearFeature = function(scene) {
            this.clearPromptingSettings(scene);
            scene.selectedFeature = null;
            this.aiFeatureErrors[this.model.scenesList.indexOf(scene)] = null;
        };
    }

    clearPromptingSettings(scene) {
        if (!scene || !scene.selectedFeature || !scene.selectedFeature.promting_settings) {
            return;
        }
        delete scene.selectedFeature.promting_settings;
    }

    isPromptingFeatureScene(scene) {
        return !!(scene && scene.selectedFeature && scene.selectedFeature.value === REPLICATE_PROMPTING_FEATURE);
    }

    isVideoChromakeyFeatureScene(scene) {
        return !!(scene && scene.selectedFeature && scene.selectedFeature.value === VIDEO_CHROMAKEY_FEATURE);
    }

    isVideoChromakeyFeatureAvailable(scene) {
        if (!scene || !scene.captureModes || scene.captureModes.length < 4) {
            return false;
        }
        const isVideoEnabled = !!scene.captureModes[3].enabled;
        const hasOtherModes = !!(scene.captureModes[0].enabled || scene.captureModes[1].enabled || scene.captureModes[2].enabled);
        if (!isVideoEnabled || hasOtherModes) {
            return false;
        }
        if (!this.activeFeatures) {
            return false;
        }
        return this.activeFeatures.some(function(item) {
            return item.feature === VIDEO_CHROMAKEY_FEATURE && item.active;
        });
    }

    isPromptingFeatureAvailable(scene) {
        if (!this.promptingEnabled || !scene || !scene.captureModes || scene.captureModes.length < 4) {
            return false;
        }

        const isPhotoEnabled = !!scene.captureModes[0].enabled;
        const hasOtherModes = !!(scene.captureModes[1].enabled || scene.captureModes[2].enabled || scene.captureModes[3].enabled);

        return isPhotoEnabled && !hasOtherModes;
    }

    normalizeSceneFeatureState(scene, index) {
        if (!scene || !scene.selectedFeature) {
            if (index !== undefined) {
                this.aiFeatureErrors[index] = null;
            }
            return;
        }

        if (!this.isPromptingFeatureScene(scene)) {
            this.clearPromptingSettings(scene);
        }

        if (this.isPromptingFeatureScene(scene) && !this.isPromptingFeatureAvailable(scene)) {
            this.clearFeature(scene);
            if (index !== undefined) {
                this.aiFeatureErrors[index] = "Remove Background (promting) can only be used with Photo mode. Please disable other capture modes.";
            }
            return;
        }

        if (this.isVideoChromakeyFeatureScene(scene) && !this.isVideoChromakeyFeatureAvailable(scene)) {
            if (index !== undefined) {
                this.aiFeatureErrors[index] = "Video Chromakey can only be used with Video capture mode. Please disable other capture modes.";
            }
            return;
        }

        if (index !== undefined) {
            this.validateAIFeatureWithCaptureModes(scene, index);
        }
    }

    findFeatureDefinition(featureValue) {
        for (let g = 0; g < this.aiFeatureGroups.length; g++) {
            const features = this.aiFeatureGroups[g].features;
            for (let f = 0; f < features.length; f++) {
                if (features[f].value === featureValue) {
                    return features[f];
                }
            }
        }
        return null;
    }

    hydrateSceneSelectedFeature(scene) {
        if (!scene || !scene.selectedFeature) {
            return;
        }

        let featureValue;
        if (typeof scene.selectedFeature === "string") {
            featureValue = scene.selectedFeature;
        } else if (scene.selectedFeature.value) {
            featureValue = scene.selectedFeature.value;
        } else {
            return;
        }

        const definition = this.findFeatureDefinition(featureValue);
        if (!definition) {
            return;
        }

        const promptingSettings = scene.selectedFeature.promting_settings;
        if (!scene.selectedFeature.name || scene.selectedFeature.value !== definition.value) {
            scene.selectedFeature = angular.copy(definition);
            if (featureValue === REPLICATE_PROMPTING_FEATURE && promptingSettings) {
                scene.selectedFeature.promting_settings = angular.copy(promptingSettings);
            }
        }
    }

    ensureSceneCaptureModes(scene) {
        if (!scene.captureModes || scene.captureModes.length < 4) {
            scene.captureModes = [
                {mode: "photo", enabled: true},
                {mode: "gif", enabled: true},
                {mode: "boomerang", enabled: true},
                {mode: "video", enabled: true}
            ];
        }
    }

    syncSceneCaptureModesFromActiveModes() {
        if (!this.activeModes || !this.model || !this.model.scenesList) {
            return;
        }
        this.model.scenesList.forEach((scene) => {
            this.ensureSceneCaptureModes(scene);
            if (!this.activeModes.photo) {
                scene.captureModes[0].enabled = false;
            }
            if (!this.activeModes.gif) {
                scene.captureModes[1].enabled = false;
            }
            if (!this.activeModes.boomerang) {
                scene.captureModes[2].enabled = false;
            }
            if (!this.activeModes.video) {
                scene.captureModes[3].enabled = false;
            }
        });
    }

    initializeSceneFeatureState() {
        if (!this.model || !this.model.scenesList) {
            return;
        }
        this.syncSceneCaptureModesFromActiveModes();
        this.model.scenesList.forEach((scene, index) => {
            this.hydrateSceneSelectedFeature(scene);
            this.normalizeSceneFeatureState(scene, index);
        });
    }

    getSceneLimit() {
        return this.aiFeature ? 40 : 20;
    }

    getCaptureModes() {
        return [
            {
                mode: "photo",
                enabled: !!(this.activeModes && this.activeModes.photo)
            },
            {
                mode: "gif",
                enabled: !!(this.activeModes && this.activeModes.gif)
            },
            {
                mode: "boomerang",
                enabled: !!(this.activeModes && this.activeModes.boomerang)
            },
            {
                mode: "video",
                enabled: !!(this.activeModes && this.activeModes.video)
            },
        ];
    }

    buildDefaultScene() {
        const sceneNumber = this.model.scenesList.length + 1;
        let title = `New scene ${sceneNumber}`;

        if (this.aiOverlay) {
            title = `New overlay ${sceneNumber}`;
        }
        if (this.aiBackground) {
            title = `NEW TARGET IMAGE ${sceneNumber}`;
        }

        return {
            updateScene: true,
            finalBackground: null,
            finalOverlay: null,
            backgroundFiles: [],
            overlayFiles: [],
            animationTime: 1.5,
            shouldOutputPhotoMirrored: true,
            enabled: true,
            title,
            customLayoutHeight: null,
            customLayoutWidth: null,
            layoutAspectRatio: "square",
            layoutHeight: this.model.layoutHeight,
            layoutWidth: this.model.layoutWidth,
            customPhotoHeight: null,
            customPhotoWidth: null,
            photoAspectRatio: "square",
            photos: cloneDeep(this.model.photos),
            timestamp: new Date().getTime(),
            previewStyle: "photo",
            captureModes: this.getCaptureModes()
        };
    }

    addSceneDirect() {
        if (this.model.scenesList.length >= this.getSceneLimit()) {
            return;
        }

        this.model.scenesList.push(this.buildDefaultScene());
        this.model.photos[0].image = true;
        this.model.photos[0].gif = false;
        this.model.photos[0].video = false;
    }

    /**
     * Initializes the controller
     * Sets up initial state and validates capture modes
     */
    $onInit() {
        if (!this.model){
            this.model = {scenesList:[]};
        } else{
            if (!this.model.scenesList) {
                this.model.scenesList = [];
            }
        }
        this.initializeSceneFeatureState();
    }

    $onChanges(changes) {
        if (!this.model || !this.model.scenesList) {
            return;
        }
        if (changes.activeFeatures || changes.activeModes || changes.promptingEnabled) {
            this.initializeSceneFeatureState();
        }
    }

    /**
     * Creates a copy of a scene
     * @param {Object} scene - The scene to copy
     * @param {number} index - Index where to insert the copy
     */
    copyScene(scene, index) {
        if (this.model.scenesList.length < 20) {
            let newScene = cloneDeep(scene);
            newScene.title = `Copy of ${scene.title}`;
            newScene.timestamp = new Date().getTime();
            delete newScene.$$hashKey;
            this.model.scenesList.splice(index + 1, 0, newScene);
        }
    }

    /**
     * Gets the file from an array of files
     * @param {Array} arr - Array of files
     * @returns {string} The file path
     */
    getFile(arr) {
        if (!arr || !arr.length) return [];
        return arr[0].attachment_file_alpha ? arr[0].attachment_file_alpha : arr[0].attachment_file;
    }

    /**
     * Deletes a scene after confirmation
     * @param {number} index - Index of the scene to delete
     */
    deleteScene(index) {
        let confirm = this.$mdDialog.confirm()
            .title("Delete scene?")
            .textContent("This deletes all backgrounds, overlays and filters of this scene and cannot be undone.")
            .ariaLabel("Scene delete")
            .ok("DELETE")
            .cancel("CANCEL");
        this.$mdDialog.show(confirm).then(() => {
            this.model.scenesList.splice(index , 1);
            this.sortByEnabled();
        });
    }

    /**
     * Opens dialog to change scene name
     * @param {Event} ev - The click event
     * @param {Object} data - The scene data
     */
    changeName(ev, data) {
        var parentEl = angular.element(document.body);
        this.$mdDialog.show({
            parent: parentEl,
            title: "Change scene title",
            controller: ChangeNameController,
            template: ChangeNameTemplate,
            targetEvent: ev,
            locals: {
                callback: (title) => {
                    data.title = title;
                },
                name: data.title,
                isUpperCase: true
            }
        });
    }

    /**
     * Scrolls the list to the top
     */
    scrollTop() {
        clearInterval(this.activeScroll);
        this.scrollList("top");
    }

    /**
     * Scrolls the list to the bottom
     */
    scrollBottom() {
        clearInterval(this.activeScroll);
        this.scrollList("bottom");
    }

    /**
     * Handles continuous scrolling
     * @param {string} direction - Direction to scroll ("top" or "bottom")
     */
    scrollList(direction) {
        let el = document.getElementById("scenes-list");
        clearInterval(this.activeScroll);
        this.activeScroll = setInterval(() => {
            el.scrollTop = direction === "top" ? el.scrollTop - 3 : el.scrollTop + 3;
            if ((el.scrollHeight === el.clientHeight + el.scrollTop) || (el.scrollTop === 0)) {
                clearInterval(this.activeScroll);
            }
        }, 20);
    }

    /**
     * Stops the scrolling animation
     */
    stopScroll() {
        clearInterval(this.activeScroll);
    }

    /**
     * Checks if top scroll button should be shown
     * @returns {boolean} Whether to show top scroll button
     */
    topScroll() {
        let el = document.getElementById("scenes-list");
        return el.scrollTop !== 0;
    }

    /**
     * Checks if bottom scroll button should be shown
     * @returns {boolean} Whether to show bottom scroll button
     */
    bottomScroll() {
        let el = document.getElementById("scenes-list");
        return el.scrollHeight !== el.clientHeight + el.scrollTop;
    }

    /**
     * Opens dialog to add or edit a scene
     * @param {Object} scene - The scene to edit (optional)
     * @param {number} index - Index of the scene to edit (optional)
     */
    addNewSceneDialog(scene, index) {
        var parentEl = angular.element(document.body);
        this.$mdDialog.show({
            parent: parentEl,
            title: "Change scene title",
            controller: AddSceneController,
            template: this.isPromptingFeatureScene(scene) ? AddPromptingSceneTemplate : AddSceneTemplate,
            escapeToClose: false,
            locals: {
                sceneNumber: this.model.scenesList.length + 1,
                company: this.company,
                scene: cloneDeep(scene),
                isEdit: !!scene,
                modes: {
                  single: this.single,
                  gif: this.gif,
                  boomerang: this.boomerang,
                  video: this.video,
                },
                globalData: {
                    layoutHeight: this.model.layoutHeight,
                    layoutWidth: this.model.layoutWidth,
                    photos: this.model.photos
                },
                aiFeature: this.aiFeature,
                aiBackground: this.aiBackground,
                aiOverlay: this.aiOverlay,
                isPromptingMode: this.isPromptingFeatureScene(scene),
                promptingQuality: this.promptingQuality,
                hideBg: this.hideBg,
                unscreenIsActive: this.unscreenIsActive,
                callback: (s) => {
                    if (scene) {
                        this.model.scenesList[index] = s;
                    } else {
                        Object.assign(s, {captureModes: this.getCaptureModes()});
                        this.model.scenesList.push(s);
                    }
                    this.model.photos[0].image = true;
                    this.model.photos[0].gif = false;
                    this.model.photos[0].video = false;
                }
            }
        });
    }

    /**
     * Calculates the height of the scenes list
     * @returns {string} The height in pixels
     */
    getListHeight() {
        if (this.model.scenesList.length < 4) {
            return 40 + 70 * this.model.scenesList.length + "px";
        } else {
            return "240px";
        }
    }

    /**
     * Sorts scenes by enabled status
     * Ensures at least one scene is enabled
     */
    sortByEnabled() {
        this.model.scenesList = orderBy(this.model.scenesList, "enabled", ["desc"]);

        if (this.model.scenesList.length && this.checkIfAllDisabled(this.model.scenesList)) {
            this.model.scenesList[0].enabled = true;
        }
    }

    /**
     * Checks if all scenes are disabled
     * @param {Array} list - List of scenes
     * @returns {boolean} Whether all scenes are disabled
     */
    checkIfAllDisabled(list) {
        let disabled = true;
        forEach(list, (s) => {
            if (s.enabled) {
               disabled = false;
            }
        });
        return disabled;
    }

    /**
     * Toggles advanced settings visibility and validates AI features
     * @param {number} index - Index of the scene
     */
    toggleAdvancedSettings(index) {
        this.expandedStates[index] = !this.expandedStates[index];
        
        // Validate AI features when expanding the settings block
        if (this.expandedStates[index]) {
            const scene = this.model.scenesList[index];
            if (scene.selectedFeature) {
                this.validateAIFeatureWithCaptureModes(scene, index);
            }
        }
    }

    /**
     * Handles feature deactivation
     * @param {string} featureValue - Value of the deactivated feature
     */
    handleFeatureDeactivation(featureValue) {
        if (!this.activeFeatures || !this.model || !this.model.scenesList) {
            return;
        }

        const feature = this.activeFeatures.find(item => item.feature === featureValue);
        if (feature && !feature.active) {
            this.model.scenesList.forEach(scene => {
                if (scene.selectedFeature && scene.selectedFeature.value === featureValue) {
                    this.clearPromptingSettings(scene);
                    scene.selectedFeature = null;
                }
            });
        }
    }
}

export default ScenesListController;



// WEBPACK FOOTER //
// ./sources/components/configurations/scenes-list/scenes-list.controller.js